class CachingService {
  cache = new Map();

  cleanKey = (key) => {
    // If the key is a URL, remove the host and keep only the path and query parameters
    if (key.startsWith("http")) {
      const url = new URL(key);
      return url.pathname + url.search; // Keep the path and query parameters
    }
    return key;
  };

  // Set item in cache with a TTL
  set(key, value, ttl, REQUEST_TYPE) {
    if (!SUPPORTED_CACHE_KEYS[REQUEST_TYPE]) {
      return;
    }
    key = this.cleanKey(key);
    const expiry = Date.now() + ttl;
    this.cache.set(key, { data: value, expiry });
  }

  update(key, value, ttl = 3600000) {
    key = this.cleanKey(key);
    if (this.cache.has(key)) {
      const expiry = Date.now() + ttl;
      this.cache.set(key, { data: value, expiry });
    } else {
      console.warn("Cache update failed, key not found:", key);
    }
  }

  get(key) {
    key = this.cleanKey(key);
    const item = this.cache.get(key);
    if (item) {
      if (Date.now() < item.expiry) {
        return item.data;
      } else {
        this.cache.delete(key);
      }
    }
    return null; // Cache miss
  }

  invalidate(keys, neglectParams = false) {
    if (Array.isArray(keys)) {
      keys.forEach((key) => {
        // Clean the key to match how it's stored (via cleanKey in set/get)
        const cleanedKey = this.cleanKey(key);
        
        // Early return if cleanedKey is null/undefined/empty
        if (!cleanedKey) {
          console.warn("Invalid cache key provided for invalidation:", key);
          return;
        }
        
        if (neglectParams) {
          // Remove all keys that match the base URL (without query params)
          // Collect keys to delete first to avoid modifying Map during iteration
          const baseKey = cleanedKey.split('?')[0]; // Remove query params for matching
          const keysToDelete = [];
          
          for (const cacheKey of this.cache.keys()) {
            const cacheKeyBase = cacheKey.split('?')[0];
            if (cacheKeyBase === baseKey) {
              keysToDelete.push(cacheKey);
            }
          }
          
          // Delete all matching keys
          keysToDelete.forEach(cacheKey => this.cache.delete(cacheKey));
        } else {
          this.cache.delete(cleanedKey);
        }
      });
    } else {
      throw new Error("Invalid keys parameter, expected an array of string");
    }
  }

  // Remove every cached entry whose (cleaned) key starts with `prefix`.
  invalidatePrefix(prefix) {
    const cleanedPrefix = this.cleanKey(prefix);
    for (const cacheKey of [...this.cache.keys()]) {
      if (cacheKey.startsWith(cleanedPrefix)) {
        this.cache.delete(cacheKey);
      }
    }
  }

  clear() {
    this.cache.clear();
  }
}

export const getCachingTime = (url) => {
  if (url.includes("/creations") || url.includes("/news")) {
    return 15 * 60 * 1000; // 15 minutes
  }

  if (url.includes("/api/notifications?limit=1&offset=0")) {
    return 9 * 1000; // 9 seconds, since we re-fetch notifications count call every 5 seconds
  }

  return 60 * 60 * 1000; // Default to 1 hour
};

export const cachingService = new CachingService();

export const invalidateCreationCache = () => {
  ["/creations"].forEach((key) => {
    cachingService.invalidate(key);
  });
};

// HYK-1138: creation detail (15 min), news detail (15 min) and profile (1 h) responses embed the
// viewer's is_subscribed / subscriber lists. Drop them after a subscribe change so reopening a
// creation, news post or profile refetches the real state instead of a stale cached one.
// News feed / per-creator news lists carry no subscriber data, so they are left alone.
export const invalidateSubscriptionCaches = () => {
  cachingService.invalidatePrefix("/api/creations/detail/");
  cachingService.invalidatePrefix("/api/news/detail/");
  cachingService.invalidatePrefix("/api/profile/");
};

export const invalidateNewsCache = () => {
  cachingService.invalidate(["/api/news?page=1&pageSize=10"]);
};

// Drop every cached news feed page and news detail. Their is_liked is per viewer
// but cached under a URL-only key, so call this whenever the viewer changes (HYK-1111).
export const invalidateViewerNewsCache = () => {
  const viewerKeys = [...cachingService.cache.keys()].filter((key) => {
    const path = key.split("?")[0];
    return path.endsWith("/api/news") || path.includes("/api/news/detail/");
  });
  cachingService.invalidate(viewerKeys);
};

export const SUPPORTED_CACHE_KEYS = {
  FETCH_ALL_NEWS_REQUEST: true,
  FETCH_NEWS_REQUEST: true,
  FETCH_NEWS_COMMENTS_REQUEST: true,
  FETCH_CREATION_COMMENTS_REQUEST: true,
  FETCH_DETAIL_NEWS_BY_ID_REQUEST: true,
  FETCH_CREATION_DETAIL_REQUEST: true,
  SET_VIEWUSER_REQUEST: true,
  FETCH_USER_CREATIONS_REQUEST: true,
};
